import {
    Component, DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {DbValidators} from "../../../../shared/validators/DbValidators";
import {User} from "../../../../user/user";
import {Observable} from "rxjs";
import {DbResponse} from "../../../../shared/interceptors/db-response";
import {Subject} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../store";
import {HostingService} from "../../../../websites/website/hosting/hosting.service";
import {filter, first, map, takeUntil} from "rxjs/operators";
import {zip} from "rxjs";
import {Config} from "../../../../config/config";
import {ICloneWebsiteChangeStepEvent} from "../clone-website.component";
import {UserSubscription, UserSubscriptionCategory} from "../../../../user/user-subscription";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {DbTextPipe} from "../../../../shared/pipes/db-text.pipe";


interface IRegionOption {
  label: string;
  value: string;
  isAvailable: boolean;
}

@Component({
    selector: 'db-clone-installation',
    templateUrl: './clone-installation.component.html',
    styleUrls: ['./clone-installation.component.scss'],
    standalone: false
})
export class CloneInstallationComponent implements OnChanges, OnInit, OnDestroy {

  // tslint:disable-next-line:no-input-rename
    @Input() configHostname: any;
  @Input('user') private _user: User;
  @Input() websiteIds: { live: number, dev: number | null };
  @Input() originalDomain: string;
  @Input() hostedWebsitesCount: number;
  @Input() hostedWebsitesLimit: number;
  @Input() userSubscription: UserSubscription;
  @Output() readonly onResize: EventEmitter<[number, number]> = new EventEmitter<[number, number]>();
  @Output() readonly onStepChange: EventEmitter<ICloneWebsiteChangeStepEvent> =
    new EventEmitter<ICloneWebsiteChangeStepEvent>();

  /** value will be the concrete id of website env */
  public websiteEnvOptions = [
    {value: null, label: 'Live'},
    {value: null, label: 'Staging'}
  ];

  public cloneInstallationForm: FormGroup = new FormGroup({
    env: new FormControl(null, Validators.required),
    subdomain: new FormControl('', [DbValidators.trim, Validators.required, Validators.pattern('^([a-z0-9]+(-[a-z0-9]+)*)$')]),
    region: new FormControl(null, Validators.required),
  });
  public regionOptions: IRegionOption[];
  public initialDataLoading: boolean = true;
  public loading: boolean = false;
  public formSubmitted: boolean = false;
  dataCenter: string;
  protected readonly UserSubscriptionCategory = UserSubscriptionCategory;

    public get shouldShowHostnameTooltip(): boolean {
        return this.configHostname && this.configHostname.length > 13;
    }

  private _regions$: Observable<IRegionOption[]>;
  private _subDomainSuggestion$: Observable<DbResponse>;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _hostingService: HostingService,
    private _dbText: DbTextPipe
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['_user'] && changes['_user'].currentValue) {
      this._user = changes['_user'].currentValue;
      this._runInitialSubscriptions();
    }
  }

  ngOnInit() {
    this.onResize.emit([640, 700]);
    this._setWebsiteIds();
    this._initializeWebsiteEnvOptions();
    this.originalDomain = this.originalDomain.replace('https://', '').replace('http://', '');
  }

  private _initializeWebsiteEnvOptions(): void {
    this.websiteEnvOptions[0].label = this._dbText.transform('text_live');
    this.websiteEnvOptions[1].label = this._dbText.transform('text_staging');
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  public validateAndProceed(): void {
    if (this.cloneInstallationForm.invalid) {
      this.formSubmitted = true;
      return;
    }
    this.loading = true;
    this._checkSubDomain();
  }

  /** auto set websiteId as env value */
  private _setWebsiteIds(): void {
    this.websiteEnvOptions[0].value = this.websiteIds.live || null;
    this.websiteEnvOptions[1].value = this.websiteIds.dev || null;
    this.cloneInstallationForm.get('env').setValue(this.websiteEnvOptions[0].value);
  }

  private _runInitialSubscriptions(): void {
    this._regions$ = this._getRegions();
    this._subDomainSuggestion$ = this._getSubdomainSuggestion();
    this._addSubscriptions();
  }

  /** run subscriptions for initialize installation popup */
  private _addSubscriptions(): void {
    this._subDomainSuggestion$
      .subscribe(response => {
        this.cloneInstallationForm.get('subdomain').setValue(response.data.subdomain.toLowerCase());
      });

    this._regions$
      .subscribe((regions: IRegionOption[]) => {
        const vpsRegion = {label: this.userSubscription?.plan?.dedicatedVMInfo?.zoneName, value: this.userSubscription?.plan?.dedicatedVMInfo?.vmZone,
          isAvailable: true  };
        this.regionOptions =  this.userSubscription?.isDedicatedVM ? [vpsRegion] : regions;
        if (this.regionOptions && this.regionOptions.length) {
          const region = this.regionOptions.find(_region => _region.isAvailable);
          this.cloneInstallationForm.controls['region'].setValue(region.value);
        }
      });

    /** wait until streams complete & display installation content */
    zip(this._regions$, this._subDomainSuggestion$)
      .subscribe(() => {
        this.initialDataLoading = false;
      });
  }

  private _getRegions(): Observable<IRegionOption[]> {
    return this._hostingService.regions(this._user.subscriptionId)
      .pipe(
        takeUntil(this._destroyStream$),
        filter(response => !!response && response.success),
        first(),
        map(response => response.data.map(({location, zone, is_available}) => ({
          label: location,
          value: zone,
          isAvailable: !!is_available
        })))
      );
  }

  private _getSubdomainSuggestion(): Observable<DbResponse> {
    return this._hostingService.subdomainSuggestion()
      .pipe(
        takeUntil(this._destroyStream$),
        filter(response => !!response && response.success && response.data && response.data.subdomain),
        first()
      );
  }

  private _checkSubDomain(): void {
    this._hostingService.checkSubDomain(this.cloneInstallationForm.value.subdomain)
      .pipe(
        filter(response => !!response),
        first()
      )
      .subscribe(response => {
        if (response.success) {
          this._emitWebsiteData();
        }
        else {
          this.cloneInstallationForm.get('subdomain').setErrors({taken: response.message});
          this.formSubmitted = true;
          this.loading = false;
        }
      });
  }

  private _emitWebsiteData(): void {
    this.onStepChange.emit({step: 'cloneProcess', options:
        {websiteData: this.cloneInstallationForm.value,
          websiteUrl: this.cloneInstallationForm?.get('subdomain').value + '.' + this.configHostname,
        dataCenter: this.cloneInstallationForm.get('region').value}});
  }

}
